import { supabase } from '../lib/supabase';

// ─── Constants ────────────────────────────────────────────────────────────────
export const PROVIDER_TYPES = [
  { value: 'barrique', label: 'Fournisseur barriques' },
  { value: 'carton', label: 'Fournisseur cartons' },
  { value: 'livreur', label: 'Livreur' },
  { value: 'fournisseur_livreur', label: 'Fournisseur + Livreur' },
  { value: 'fourniture', label: 'Fournisseur fournitures' },
  { value: 'autre', label: 'Autres' },
];

export const PAYMENT_MODES = [
  { value: 'prestataire_garde', label: 'Le prestataire garde sa part directement' },
  { value: 'adt_reverse', label: 'ADT lui reverse après encaissement' },
  { value: 'comptant', label: 'Paiement comptant' },
  { value: 'facture_mensuelle', label: 'Facture mensuelle' },
];

export const PRODUCT_TYPES = [
  { value: 'barrique_noire_270l', label: 'Barrique noire 270 L' },
  { value: 'barrique_bleue_220l', label: 'Barrique bleue 220 L' },
  { value: 'carton_60x50x40', label: 'Carton 60×50×40' },
  { value: 'carton_80x50x50', label: 'Carton 80×50×50' },
  { value: 'autre', label: 'Autre produit' },
];

// ─── Provider CRUD ────────────────────────────────────────────────────────────
export const providerService = {

  async getAll({ activeOnly = false } = {}) {
    let query = supabase
      ?.from('carton_suppliers')
      ?.select('*')
      ?.order('name', { ascending: true });

    if (activeOnly) {
      query = query?.eq('status', 'actif');
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || [])?.map(mapProvider);
  },

  async getById(id) {
    const { data, error } = await supabase
      ?.from('carton_suppliers')
      ?.select('*')
      ?.eq('id', id)
      ?.single();
    if (error) throw error;
    return mapProvider(data);
  },

  async create(providerData) {
    const payload = buildProviderPayload(providerData);
    const { data, error } = await supabase
      ?.from('carton_suppliers')
      ?.insert(payload)
      ?.select()
      ?.single();
    if (error) throw error;
    return mapProvider(data);
  },

  async update(id, providerData) {
    const payload = buildProviderPayload(providerData);
    payload.updated_at = new Date()?.toISOString();
    const { data, error } = await supabase
      ?.from('carton_suppliers')
      ?.update(payload)
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return mapProvider(data);
  },

  async delete(id) {
    const { error } = await supabase
      ?.from('carton_suppliers')
      ?.delete()
      ?.eq('id', id);
    if (error) throw error;
    return true;
  },

  // ─── Provider Products ──────────────────────────────────────────────────────
  async getProductsByProvider(providerId) {
    const { data, error } = await supabase
      ?.from('provider_products')
      ?.select('*')
      ?.eq('provider_id', providerId)
      ?.order('created_at', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(mapProduct);
  },

  async getActiveProductsByProvider(providerId) {
    const { data, error } = await supabase
      ?.from('provider_products')
      ?.select('*')
      ?.eq('provider_id', providerId)
      ?.eq('is_active', true)
      ?.order('created_at', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(mapProduct);
  },

  async createProduct(productData) {
    const payload = {
      provider_id: productData?.providerId,
      product_name: productData?.productName,
      product_type: productData?.productType || 'autre',
      purchase_price: Number(productData?.purchasePrice) || 0,
      client_price: Number(productData?.clientPrice) || 0,
      is_active: productData?.isActive !== false,
      product_notes: productData?.productNotes || null,
    };
    const { data, error } = await supabase
      ?.from('provider_products')
      ?.insert(payload)
      ?.select()
      ?.single();
    if (error) throw error;
    return mapProduct(data);
  },

  async updateProduct(id, productData) {
    const payload = {
      product_name: productData?.productName,
      product_type: productData?.productType || 'autre',
      purchase_price: Number(productData?.purchasePrice) || 0,
      client_price: Number(productData?.clientPrice) || 0,
      is_active: productData?.isActive !== false,
      product_notes: productData?.productNotes || null,
    };
    const { data, error } = await supabase
      ?.from('provider_products')
      ?.update(payload)
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return mapProduct(data);
  },

  async deleteProduct(id) {
    const { error } = await supabase
      ?.from('provider_products')
      ?.delete()
      ?.eq('id', id);
    if (error) throw error;
    return true;
  },

  // ─── History Stats ──────────────────────────────────────────────────────────
  async getProviderHistoryStats(providerId) {
    const { data, error } = await supabase
      ?.rpc('get_provider_history_stats', { p_provider_id: providerId });
    if (error) throw error;
    return data?.[0] || { total_orders: 0, total_revenue: 0, total_provider_cost: 0, total_adt_revenue: 0, total_barrels: 0 };
  },

  async getProviderOrders(providerId) {
    const { data, error } = await supabase
      ?.from('promo_summer_orders')
      ?.select('*')
      ?.eq('provider_id', providerId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function mapProvider(s) {
  if (!s) return null;
  return {
    id: s?.id,
    name: s?.name,
    contactName: s?.contact_name,
    phone: s?.phone,
    email: s?.email,
    address: s?.address,
    city: s?.city,
    postalCode: s?.postal_code,
    country: s?.country,
    supplierType: s?.supplier_type || 'autre',
    interventionZone: s?.intervention_zone,
    paymentMode: s?.payment_mode || 'prestataire_garde',
    status: s?.status || 'actif',
    internalNotes: s?.internal_notes,
    leadTimeDays: s?.lead_time_days,
    notes: s?.notes,
    isActive: s?.is_active !== false,
    createdAt: s?.created_at,
    updatedAt: s?.updated_at,
  };
}

function mapProduct(p) {
  if (!p) return null;
  return {
    id: p?.id,
    providerId: p?.provider_id,
    productName: p?.product_name,
    productType: p?.product_type,
    purchasePrice: Number(p?.purchase_price) || 0,
    clientPrice: Number(p?.client_price) || 0,
    adtMargin: Number(p?.adt_margin) || 0,
    isActive: p?.is_active !== false,
    productNotes: p?.product_notes,
    createdAt: p?.created_at,
    updatedAt: p?.updated_at,
  };
}

function buildProviderPayload(d) {
  return {
    name: d?.name,
    contact_name: d?.contactName || null,
    phone: d?.phone || null,
    email: d?.email || null,
    address: d?.address || null,
    city: d?.city || null,
    postal_code: d?.postalCode || null,
    country: d?.country || 'France',
    supplier_type: d?.supplierType || 'autre',
    intervention_zone: d?.interventionZone || null,
    payment_mode: d?.paymentMode || 'prestataire_garde',
    status: d?.status || 'actif',
    internal_notes: d?.internalNotes || null,
    lead_time_days: d?.leadTimeDays || null,
    notes: d?.notes || null,
    is_active: d?.status === 'actif' || d?.isActive !== false,
  };
}
